<template>
  <div class="hb-rich-text rounded-lg border bg-white dark:bg-gray-800/80 overflow-hidden transition-shadow focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent"
    :class="invalid ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'">
    <div ref="editor"></div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import Quill from 'quill'
import { sanitizeHellobarHtml } from 'controllers/vue_components/hellobar/richText.js'

// A deliberately tiny editor for a one line bar: bold, italic, underline and
// links only. The shared fields/quill_editor has a full writing toolbar (headings,
// lists, tables, images) that the hellobar cannot render, so it is not reused.
const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  invalid: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const editor = ref(null)
let quill = null
let lastEmitted = ''

function load(html) {
  quill.setContents([], 'silent')
  if (html) quill.clipboard.dangerouslyPasteHTML(0, html, 'silent')
}

onMounted(() => {
  quill = new Quill(editor.value, {
    theme: 'snow',
    placeholder: props.placeholder,
    // Limits what paste can bring in, not just what the toolbar offers.
    formats: ['bold', 'italic', 'underline', 'link'],
    modules: {
      toolbar: [['bold', 'italic', 'underline'], ['link'], ['clean']],
      keyboard: {
        bindings: {
          // The bar is a single line, so Enter never starts a new paragraph.
          singleLine: { key: 'Enter', shiftKey: null, handler: () => false },
        },
      },
    },
  })

  load(props.modelValue)
  lastEmitted = props.modelValue

  quill.on('text-change', () => {
    lastEmitted = quill.root.innerHTML
    emit('update:modelValue', lastEmitted)
  })
})

// Only reload when the value really changed from outside (a retry or reset).
// After a save the parent hands back the server's sanitized copy of what was
// typed; reloading that would jump the caret to the start for no visible change.
watch(() => props.modelValue, (value) => {
  if (!quill) return
  if (sanitizeHellobarHtml(value) === sanitizeHellobarHtml(lastEmitted)) return
  load(value)
  lastEmitted = value
})

onBeforeUnmount(() => {
  quill?.off('text-change')
  quill = null
})
</script>

<style scoped>
/* Quill (snow theme) owns this markup, so it is styled with raw CSS per the
   CSS vs Tailwind rule in CLAUDE.md. */
.hb-rich-text :deep(.ql-toolbar.ql-snow) {
  border: 0;
  border-bottom: 1px solid rgb(243 244 246);
  padding: 6px 8px;
}
.hb-rich-text :deep(.ql-container.ql-snow) {
  border: 0;
  font-family: inherit;
  font-size: 14px;
}
.hb-rich-text :deep(.ql-editor) {
  min-height: 72px;
  padding: 10px 14px;
  line-height: 1.5;
}
.hb-rich-text :deep(.ql-editor.ql-blank::before) {
  left: 14px;
  right: 14px;
  font-style: normal;
  color: rgb(156 163 175);
}
:global(.dark) .hb-rich-text :deep(.ql-toolbar.ql-snow) {
  border-bottom-color: rgb(55 65 81);
}
:global(.dark) .hb-rich-text :deep(.ql-snow .ql-stroke) {
  stroke: rgb(209 213 219);
}
:global(.dark) .hb-rich-text :deep(.ql-editor) {
  color: rgb(243 244 246);
}
</style>
